import React from "react";
import {
  Box,
  Divider,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  alpha,
} from "@mui/material";
import CustomizeDatePicker from "components/DatePicker";
import OptionProvince from "components/OptionProvince";
import OptionCarBrands from "./OptionCarBrands";
import { GradientButtonOutline } from "components/GradientButton";
import { formatCurrency2Digit } from "utils/helpers/format";
import { useCarStatuses } from "contexts/CarStatusContext";

const RenderDetailCar = ({ car, type = "", ...props }) => {
  const { carStatuses } = useCarStatuses();

  return (
    <Box {...props}>
      <Typography
        variant="h5"
        sx={{
          mb: 1,
        }}
      >
        ข้อมูลรถ
      </Typography>
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid #E0E0E0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              whiteSpace: "nowrap",
              padding: 2,
              fontSize: "20px",
              color: "rgba(0, 0, 0, 0.6)",
              fontWeight: 300,
              textShadow: "1px 1px 2px rgba(0, 0, 0, 0.1)",
            }}
          >
            {car?.car_name}
          </Typography>
          {(type === "pawn_car" || type === "redeem_car") && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                mr: 2,
              }}
            >
              <GradientButtonOutline
                sx={{
                  px: 3,
                  height: "40px",
                  backgroundColor: alpha("#00BB9F", 0.2),
                  borderColor: alpha("#00BB9F", 0.5),
                  cursor: "default",
                  "&:hover": {
                    backgroundColor: alpha("#00BB9F", 0.2),
                  },
                }}
                disableTouchRipple
              >
                {car?.car_type}
              </GradientButtonOutline>
              <GradientButtonOutline
                sx={{
                  px: 3,
                  height: "40px",
                  backgroundColor: alpha("#00BB9F", 0.2),
                  borderColor: alpha("#00BB9F", 0.5),
                  cursor: "default",
                  "&:hover": {
                    backgroundColor: alpha("#00BB9F", 0.2),
                  },
                }}
                disableTouchRipple
              >
                {formatCurrency2Digit(Number(car?.car_price || 0))} บาท
              </GradientButtonOutline>
            </Box>
          )}
        </Box>
        <Divider
          sx={{
            background: "linear-gradient(90deg, #00BB9F, #B0EE93)",
            height: "4px",
            boxShadow: "none",
            border: "none",
          }}
        />
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ชื่อรถ
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกชื่อรถ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.car_name || ""}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ประเภทรถ
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Autocomplete
                  fullWidth
                  options={["รถยนต์", "มอเตอร์ไซต์"]}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="เลือกประเภทรถ"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  )}
                  value={car?.car_type || ""}
                  sx={{ width: "100%" }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ทะเบียนรถ
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกทะเบียนรถ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.license_plate || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={1}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  จังหวัด
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={4}>
                <OptionProvince value={car?.license_province || ""} disabled />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เลขตัวรถ
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกเลขตัวรถ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.car_no || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  วันที่จดทะเบียน
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <CustomizeDatePicker
                  key={"license_date"}
                  value={car?.license_date || null}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  วันหมดอายุภาษี
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <CustomizeDatePicker
                  key={"tax_expire_date"}
                  value={car?.tax_expire_date || null}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  วันหมดอายุพ.ร.บ.
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <CustomizeDatePicker
                  key={"compulsory_expire_date"}
                  value={car?.compulsory_expire_date || null}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ลักษณะ
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกลักษณะ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.characteristic || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ยี่ห้อ
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={5}>
                <OptionCarBrands value={car?.car_brand_id} readOnly disabled />
              </Grid>
              <Grid item xs={2} sm={2} md={1}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  สี
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกสี"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.color || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  รุ่น
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกรุ่น"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.model || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ชนิดน้ำมัน
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกชนิดน้ำมัน"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.fuel_type || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ยื่ห้อเครื่องยนต์
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกยี่ห้อเครื่องยนต์"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.engine_brand || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เลขเครื่องยนต์
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกเลขเครื่องยนต์"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.engine_number || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  เลขถังแก๊ส
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกเลขถังแก๊ส"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.gas_number || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  จำนวนสูบ
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={2.5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกจำนวนสูบ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.number_of_engine_cylinders || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={1}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  ซีซี
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={2.5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกซีซี"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.cc || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={1.5}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  แรงม้า
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={2.5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกแรงม้า"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.horsepower || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  น้ำหนักรถ (กก.)
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={2.5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกน้ำหนักรถ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.weight || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={1}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  ที่นั่ง
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={2.5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกที่นั่ง"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.seat || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={1.5}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  เลขไมล์รถ
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={2.5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="0"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.car_mileage || null}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ราคา
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="0"
                  type="number"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.car_price || null}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ประกันรถ
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="ถ้ามีประกัน กรุณากรอกประกันรถ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.insurance_company || ""}
                  disabled
                />
              </Grid>
              <Grid item xs={2} sm={2} md={2}>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: {
                      xs: "left",
                      sm: "left",
                      md: "right",
                    },
                  }}
                >
                  ชั้น
                </Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกชั้นประกัน"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.insurance_level || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1">วันหมดอายุประกันรถ</Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={10}>
                <CustomizeDatePicker
                  value={car?.insurance_expire_date || null}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1">รูปแบบการซื้อรถ</Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={10}>
                <FormControl component="fieldset" disabled>
                  <RadioGroup
                    row
                    name="carPurchaseType"
                    value={car?.purchase_type || "cash"}
                    sx={{ display: "flex", gap: 2 }}
                  >
                    <FormControlLabel
                      value="cash"
                      control={<Radio />}
                      label="เงินสด"
                    />
                    <FormControlLabel
                      value="installment"
                      control={<Radio />}
                      label="เงินผ่อน"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1">รอยตำหนิ</Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกรอยตำหนิ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.defect || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1">สถานะรถ</Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={10}>
                <Autocomplete
                  fullWidth
                  options={carStatuses}
                  getOptionLabel={(option) => option.name ?? ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="เลือกสถานะรถ"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  )}
                  value={
                    carStatuses.find(
                      (status) => status.id === car?.car_status_id
                    ) || null
                  }
                  sx={{ width: "100%" }}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={2} sm={2} md={2}>
                <Typography variant="body1">หมายเหตุ</Typography>
              </Grid>
              <Grid item xs={10} sm={10} md={10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="กรุณากรอกหมายเหตุ"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  value={car?.note || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="body1">รูปรถ</Typography>
            <Typography variant="body1">
              {car?.car_pictures?.length
                ? car?.car_pictures?.length
                : car?.pictures?.length ?? 0}
              /5
            </Typography>
          </Box>

          {car?.car_pictures && car?.car_pictures.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
              }}
            >
              {car?.car_pictures.map((picture, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "calc(20% - 10px)",
                    height: "100px",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <img
                    src={picture.url ?? picture}
                    alt={`Car ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : car?.pictures && car?.pictures.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
              }}
            >
              {car?.pictures.map((picture, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "calc(20% - 10px)",
                    height: "100px",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <img
                    src={picture}
                    alt={`Car ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              ไม่มีรูปภาพรถ
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RenderDetailCar;
